import React, { useState } from 'react';
import { Folder, FileText, File, Eye, Pencil, Trash2, Download, Loader2 } from 'lucide-react';

import useDrive from '../../hooks/useDrive';
import {
  useDeleteDocument,
  useViewDocument,
} from '../../../UploadDocuments/hooks/Useuploaddocuments';

import { Card, IconWrapper, FileName, Meta, Actions, ActionButton } from './Grid.styles';
import RenameDocumentModal from '../../modals/RenameDocumentModal';

const renderIcon = (item) => {
  if (item.type === 'folder') return <Folder size={42} color="#F4B400" />;
  if (item.extension === 'pdf') return <FileText size={42} color="#EA4335" />;
  return <File size={42} color="#4285F4" />;
};

const GridCard = ({ item, onOpen }) => {
  const { setItems, setPreviewOpen, setPreviewFile, applicationId } = useDrive();
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [busyAction, setBusyAction] = useState(null); // 'viewing' | 'downloading' | 'deleting'

  const isFolder = item.type === 'folder';

  const { mutate: viewDoc } = useViewDocument({
    onSuccess: (data, variables) => {
      const url = data?.documentUrl ?? data?.url;
      setBusyAction(null);

      if (variables.intent === 'download') {
        if (url) window.open(url, '_blank');
        return;
      }

      // 'preview' intent: hand the fetched url off to the drawer.
      setPreviewFile({ ...item, url });
      setPreviewOpen(true);
    },
    onError: (err) => {
      setBusyAction(null);
      window.alert(err?.message || 'Failed to open document.');
    },
  });

  const { mutate: deleteDoc, isPending: isDeleting } = useDeleteDocument({
    onSuccess: (_, { documentId }) => {
      setItems((prev) => prev.filter((x) => x.documentId !== documentId));
    },
    onError: (err) => window.alert(err?.message || 'Failed to delete document.'),
  });

  const handlePreview = (e) => {
    e.stopPropagation();
    if (isFolder) return;
    setBusyAction('viewing');
    viewDoc({ documentId: item.documentId, applicationId, intent: 'preview' });
  };

  const handleRename = () => {
    if (isFolder) return;
    setIsRenameOpen(true);
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    if (isFolder) return;
    setBusyAction('downloading');
    viewDoc({ documentId: item.documentId, applicationId, intent: 'download' });
  };

  const handleDelete = (e) => {
    e.stopPropagation();

    if (isFolder) {
      window.alert('Deleting folders is not supported yet.');
      return;
    }

    if (!window.confirm(`Delete "${item.name}"?`)) return;
    deleteDoc({ documentId: item.documentId, applicationId });
  };

  const isViewing = busyAction === 'viewing';
  const isDownloading = busyAction === 'downloading';

  return (
    <Card onDoubleClick={() => onOpen(item)}>
      <IconWrapper>{renderIcon(item)}</IconWrapper>
      <FileName>{item.name}</FileName>
      <Meta>{item.modified}</Meta>
      <Meta>{item.size}</Meta>

      {!isFolder && (
        <Actions>
          <ActionButton onClick={handlePreview} disabled={isViewing || isDownloading || isDeleting}>
            {isViewing ? <Loader2 size={16} className="spin" /> : <Eye size={16} />}
          </ActionButton>

          <ActionButton onClick={handleRename} disabled={isViewing || isDownloading || isDeleting}>
            <Pencil size={16} />
          </ActionButton>

          <ActionButton
            onClick={handleDownload}
            disabled={isViewing || isDownloading || isDeleting}
          >
            {isDownloading ? <Loader2 size={16} className="spin" /> : <Download size={16} />}
          </ActionButton>

          <ActionButton
            $danger
            onClick={handleDelete}
            disabled={isViewing || isDownloading || isDeleting}
          >
            {isDeleting ? <Loader2 size={16} className="spin" /> : <Trash2 size={16} />}
          </ActionButton>
        </Actions>
      )}

      <RenameDocumentModal
        isOpen={isRenameOpen}
        onClose={() => setIsRenameOpen(false)}
        doc={{ id: item.documentId, name: item.name }}
        applicationId={applicationId}
      />
    </Card>
  );
};

export default GridCard;
